import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { NumberParam, StringParam, useQueryParams } from 'use-query-params';

import { ComponentModulesOpenWooRequestOverview } from 'api/graphql-types';
import { searchWooDocuments } from 'api/algolia';
import { useAppSelector } from 'redux/store';
import { IActiveFilter } from 'redux/stores/subsidy';
import { WOORequestAlgoliaFragment } from 'redux/stores/woo-requests';
import { useDebounce } from 'utils/use-debounce';
import { ISortOption } from 'components/sort';
import { DATE_FILTER_TYPE, DateFilterValue } from 'components/date-filter-button/date-filter-data';
import {
	DATE_FILTER_QUERY_KEY,
	DateFilterParam,
	useDateFilterButton
} from 'components/date-filter-button/use-date-filter-button';
import { Search } from 'components/search/search';
import { Card } from 'components/card';
import { FilterSidebar } from 'components/filter-sidebar';
import { Link } from 'components/link/link';
import { Icon } from 'components/icon/icon';

import { useWooRequestsFilters } from './data';
import { MobileActions } from './components';
import { CATEGORY_QUERY_KEY, CategoryArrayParam, useCategory } from './queries';

import IconChevronRight from 'public/assets/icons/chevron-right.svg';

import styles from './woo-document-overview.module.scss';
import {
	PAGE_QUERY_KEY,
	SEARCH_QUERY_KEY,
	SORT_QUERY_KEY,
	NewOldSortParam,
	usePagination,
	useSearch,
	useSort
} from 'utils/hooks/queries';

export const WOO_REQUEST_OVERVIEW_LIMIT = 8;

type FetchWooDocuments = (
	page: number,
	newSort: ISortOption,
	newSearchValue: string | null,
	categoryIds: string[],
	startDate?: Date,
	endDate?: Date
) => Promise<void>;

type WooDocumentFetchProps = {
	page: number;
	sort: ISortOption;
	searchValue: string;
	categoryIds: string[];
	startDate?: Date | null;
	endDate?: Date | null;
};

export type WooDocumentOverviewProps = {
	module: ComponentModulesOpenWooRequestOverview;
};

const WooDocumentOverview = ({ module }: WooDocumentOverviewProps): React.JSX.Element => {
	const router = useRouter();
	const { formatMessage } = useIntl();

	const [query, setQuery] = useQueryParams({
		[PAGE_QUERY_KEY]: NumberParam,
		[SORT_QUERY_KEY]: NewOldSortParam,
		[SEARCH_QUERY_KEY]: StringParam,
		[CATEGORY_QUERY_KEY]: CategoryArrayParam,
		[DATE_FILTER_QUERY_KEY]: DateFilterParam
	});

	const overviewHeaderRef = useRef<HTMLDivElement>(null);
	const { wooRequests, wooRequestsCount } = useAppSelector((state) => state.wooRequests);

	const [requests, setRequests] = useState<WOORequestAlgoliaFragment[]>(wooRequests || []);
	const [requestIsLoading, setRequestIsLoading] = useState<boolean>(false);
	const [requestCount, setRequestCount] = useState<number>(wooRequestsCount);

	const [sortIsOpen, setSortIsOpen] = useState<boolean>(false);
	const [filtersIsOpen, setFiltersIsOpen] = useState<boolean>(false);
	const [dateFilterIsOpen, setDateFilterIsOpen] = useState<boolean>(false);
	const [initialQuery, setInitialQuery] = useState<typeof query | undefined>();
	const [hasFiltered, setHasFiltered] = useState<boolean>(false);

	const { currentPage, setCurrentPage, Pagination } = usePagination(WOO_REQUEST_OVERVIEW_LIMIT);
	const { sort, setSort, Sort } = useSort('wooRequests', 'newOld');
	const { searchValue, setSearchValue } = useSearch();
	const { dateFilter, setDateFilter, DateFilterButton } = useDateFilterButton();
	const { selectedCategories, setSelectedCategoriesParam } = useCategory();

	const [search, setSearch] = useState<string | null>(searchValue);
	const debouncedSearch = useDebounce(search, 800);

	const fetchWooDocuments: FetchWooDocuments = async (
		page,
		newSort,
		newSearchValue,
		categoryIds,
		startDate,
		endDate
	): Promise<void> => {
		setRequestIsLoading(true);

		try {
			const { results: woo, total } = await searchWooDocuments(
				newSearchValue || '',
				{
					categoryIds: categoryIds ?? [],
					startDate,
					endDate
				},
				{
					sort: newSort?.id === 'asc' ? 'asc' : 'desc',
					page,
					limit: WOO_REQUEST_OVERVIEW_LIMIT,
					locale: router.locale
				}
			);

			setRequests(woo);
			setRequestCount(total);
		} catch {
			setRequests([]);
			setRequestCount(0);
		}

		setRequestIsLoading(false);
	};

	const scrollToTop = (): void => {
		if (typeof window !== 'undefined') {
			const extraOffset = 140;
			const moduleOffset = (overviewHeaderRef.current?.getBoundingClientRect().top || 0) + window.scrollY - extraOffset;
			window.scrollTo({ top: moduleOffset, left: 0, behavior: 'smooth' });
		}
	};

	const handleDeleteFilter = (item?: IActiveFilter): void => {
		if (item?.label) {
			const newSelected = selectedCategories.filter((selection) => selection.label !== item.label);
			setSelectedCategoriesParam(newSelected.length > 0 ? newSelected : null);
			handleFetchDocument({ page: 1, categoryIds: newSelected.map((newItem) => String(newItem.id)) });
		} else {
			setQuery({
				categorien: null,
				pagina: 1,
				datum: null,
				sort: null,
				zoekterm: null
			});
			setSearch('');
			handleFetchDocument(undefined, true);
		}
	};

	const handleDateOnChange = (value: DateFilterValue): void => {
		if (!setDateFilter) return;
		setDateFilter(value);

		const isAll = value.type === DATE_FILTER_TYPE.ALL;
		const endDate = isAll ? null : value.endDate;
		const startDate = isAll ? null : value.startDate;

		handleFetchDocument({ page: 1, startDate, endDate });

		if (currentPage !== 1) {
			setCurrentPage(1);
		}
	};

	const handleSortOnChange = (selectedSortFilter: { id: string; label: string } | undefined): void => {
		if (selectedSortFilter) {
			setSort(selectedSortFilter);
			handleFetchDocument({ page: 1, sort: selectedSortFilter });

			if (currentPage !== 1) {
				setCurrentPage(1);
			}
		}
	};

	const handleSearchOnChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
		setSearch(event.target.value);

		if (currentPage !== 1) {
			setCurrentPage(1);
		}
	};

	const handlePageChange = (page: number): void => {
		if (page !== currentPage) {
			setCurrentPage(page);
			handleFetchDocument({ page });
		}
	};

	const handleFetchDocument = async (data?: Partial<WooDocumentFetchProps>, reset?: boolean): Promise<void> => {
		const selectedPage = reset ? 1 : data?.page || currentPage;
		const selectedSort = reset ? { id: '', label: '' } : data?.sort || sort;
		const selectedSearch = reset ? '' : data?.searchValue || debouncedSearch;

		const selectedCategoryIds = reset
			? []
			: data?.categoryIds || selectedCategories.map((category) => String(category.id));

		const selectedStartDate = reset
			? undefined
			: data?.startDate === undefined
			? dateFilter?.startDate
			: data?.startDate;

		const selectedEndDate = reset ? undefined : data?.endDate === undefined ? dateFilter?.endDate : data?.endDate;

		scrollToTop();
		setHasFiltered(data !== undefined);
		fetchWooDocuments(
			selectedPage,
			selectedSort,
			selectedSearch,
			selectedCategoryIds,
			selectedStartDate || undefined,
			selectedEndDate || undefined
		);
	};

	const wooRequestFilters = useWooRequestsFilters((categoryIds: string[]) =>
		handleFetchDocument({ page: 1, categoryIds })
	);

	useEffect(() => {
		handleFetchDocument(undefined);
	}, [initialQuery]);

	useEffect(() => {
		setSearchValue(debouncedSearch || null);

		if (debouncedSearch !== null) {
			handleFetchDocument({ page: 1 });
		}
	}, [debouncedSearch]);

	useEffect(() => {
		if (
			!hasFiltered &&
			!initialQuery &&
			(query[CATEGORY_QUERY_KEY] || query[DATE_FILTER_QUERY_KEY] || query[SEARCH_QUERY_KEY] || query[SORT_QUERY_KEY])
		) {
			setInitialQuery(query);
		}
	}, [query]);

	useEffect(() => {
		if (!!query[SEARCH_QUERY_KEY] && initialQuery?.[SEARCH_QUERY_KEY] !== query[SEARCH_QUERY_KEY]) {
			setSearch(query[SEARCH_QUERY_KEY]);
		}
	}, [query[SEARCH_QUERY_KEY]]);

	return (
		<>
			<div className={styles.header} ref={overviewHeaderRef}>
				<div className="container">
					<h2>{formatMessage({ id: 'open.woo.overview.title' })}</h2>
					<span className={styles.searchContainer}>
						<Search
							searchValue={search || ''}
							searchPlaceholderText={formatMessage({ id: 'open.woo.overview.filter.search.placeholder' })}
							handleSearchOnChange={handleSearchOnChange}
							small
						/>
					</span>
				</div>
			</div>

			<div className={styles.overview}>
				<div className="container">
					<div className={styles.overviewContainer}>
						<MobileActions
							sortIsOpen={sortIsOpen}
							setSortIsOpen={setSortIsOpen}
							dateFilterIsOpen={dateFilterIsOpen}
							setDateFilterIsOpen={setDateFilterIsOpen}
							filtersIsOpen={filtersIsOpen}
							setFiltersIsOpen={setFiltersIsOpen}
						/>

						<div className={clsx(styles.filter, filtersIsOpen && styles.isOpen)}>
							<FilterSidebar
								module={module}
								mobileIsOpen={filtersIsOpen}
								setMobileIsOpen={setFiltersIsOpen}
								filters={wooRequestFilters}
								handleDeleteFilters={handleDeleteFilter}
								handleResetFilter={handleDeleteFilter}
								amountOfActiveFilters={selectedCategories.length > 0 ? 1 : 0}
								activeFilters={(selectedCategories || []) as IActiveFilter[]}
							/>
						</div>

						{module.filterLink?.href?.text && module.filterLink?.href?.url && (
							<div className={styles.filterLink}>
								<Link href={module.filterLink.href.url} variant="tertiary-dark">
									<span className={styles.description}>
										<span>
											<strong>{module.filterLink.href.text}</strong>
											{module.filterLink.description && module.filterLink.description}
										</span>
										<Icon customIcon={<IconChevronRight />} size="12px" />
									</span>
								</Link>
							</div>
						)}

						<div className={styles.content}>
							<div className={styles.resultsHeader}>
								<h3 id="Woo-documenten">{formatMessage({ id: 'open.woo.overview.content.title' })}</h3>
								<div className={styles.sortBar}>
									<span id="pageResults" aria-live="polite">
										{formatMessage({ id: 'global.results' }, { results: requestCount })}
									</span>
									<div style={{ display: 'flex' }}>
										<DateFilterButton
											title={formatMessage({ id: 'open.woo.overview.filter.date' })}
											onChange={handleDateOnChange}
											value={dateFilter}
											setMobileIsOpen={setDateFilterIsOpen}
											mobileIsOpen={dateFilterIsOpen}
											initialEndDate={query[DATE_FILTER_QUERY_KEY]?.endDate}
											initialStartDate={query[DATE_FILTER_QUERY_KEY]?.startDate}
											showDates={query[DATE_FILTER_QUERY_KEY]?.type === DATE_FILTER_TYPE.CUSTOM}
										/>
										<Sort mobileIsOpen={sortIsOpen} setMobileIsOpen={setSortIsOpen} onChange={handleSortOnChange} />
									</div>
								</div>
							</div>

							<div className={clsx(styles.wooDocuments, requestIsLoading && styles.wooDocumentsLoading)}>
								{requests.length > 0 ? (
									requests.map((wooRequest) => (
										<Card
											key={`permit-${wooRequest?.title}`}
											title={wooRequest?.shortTitle || wooRequest?.title}
											slug={wooRequest.slug}
											type={wooRequest.category?.title}
											file={wooRequest.zip}
											date={wooRequest.startDate}
										/>
									))
								) : (
									<div className={styles.noResults}>
										<h4>{formatMessage({ id: 'global.noResults' })}</h4>
										<button type="button" onClick={() => handleDeleteFilter()}>
											{formatMessage({ id: 'global.resetFilters' })}
										</button>
									</div>
								)}
							</div>
						</div>

						<div className={styles.pagination}>
							<Pagination totalCount={requestCount} onPageChange={handlePageChange} />
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default WooDocumentOverview;
